import { useApiPost } from '~/composables/useApiPost'

interface LikeResponse {
  status: string
  message: string
  data: unknown
}

export const useLikes = () => {
  const { getToken, isLiked, addLikeId, removeLikeId } = useUser()
  const { t } = useI18n()

  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const getLikeHeaders = (): Record<string, string> => {
    const token = getToken()
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
    return headers
  }

  // Add like
  const addLike = async (newsId: number): Promise<LikeResponse | null> => {
    try {
      isLoading.value = true
      error.value = null

      const token = getToken()
      const body: Record<string, unknown> = { news_id: newsId }
      if (!token) {
        body.fingerprint_id = useFingerprintStore().visitorId
      }

      const { data: response, error: apiError } = await useApiPost<LikeResponse>(
        '/add-like',
        body,
        getLikeHeaders()
      )

      if (apiError) throw new Error('API Error')

      if (response?.status === 'success') {
        addLikeId(newsId)
      }

      return response
    } catch (err: unknown) {
      console.error('addLike error:', err)
      error.value = (err as Error).message || t('widget.news_page.details.add_to_likes_error')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Remove like
  const removeLike = async (newsId: number): Promise<LikeResponse | null> => {
    try {
      isLoading.value = true
      error.value = null

      const token = getToken()
      const body: Record<string, unknown> = { news_id: newsId }
      if (!token) {
        body.fingerprint_id = useFingerprintStore().visitorId
      }

      const { data: response, error: apiError } = await useApiPost<LikeResponse>(
        '/remove-like',
        body,
        getLikeHeaders()
      )

      if (apiError) throw new Error('API Error')

      if (response?.status === 'success') {
        removeLikeId(newsId)
      }

      return response
    } catch (err: unknown) {
      error.value = (err as Error).message || t('widget.news_page.details.remove_from_likes_error')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Toggle like status
  const toggleLike = async (
    newsId: number,
    onSuccess?: () => void
  ): Promise<LikeResponse | null> => {
    const wasLiked = isLiked.value(newsId)
    let result: LikeResponse | null = null

    if (wasLiked) {
      result = await removeLike(newsId)
    } else {
      result = await addLike(newsId)
    }

    if (result?.status === 'success' && onSuccess) {
      onSuccess()
    }

    return result
  }

  return {
    isLoading: readonly(isLoading),
    error: readonly(error),
    isLiked,
    addLike,
    removeLike,
    toggleLike
  }
}
